import { RenderResult, KompisElement, property, html } from '@inter-ikea-kompis/web-component';
import {
	Scene,
	TAG_GROUND,
	ISceneDelegate,
	TransformPosition,
	Node,
	Camera,
	PointerHit,
	MeshRenderer,
	Interactor,
	Collider,
	BoxDimensions,
	Report,
	AssetsPackage,
} from '@ikea-aoa/elements-3d';
import { TransformVector } from './utils';
import View3dEvents from '../constants/View3dEvents';
import View3dErrors from '../constants/View3dErrors';
import { View3dApi } from '.';

/**
 * View3d component.
 */
export default class View3d extends KompisElement implements ISceneDelegate, View3dApi {
	public static eventTypes = [
		// Triggered when scene is ready.
		View3dEvents.sceneReady,
		// Triggered when all models are preloaded.
		View3dEvents.modelsPreloaded,
		// Trigger when pointer is targeting the node
		View3dEvents.onPointerOverNode,
		// Trigger when pointer is targeting the ground
		View3dEvents.onPointerOverGround,
		// Trigger on Collision
		View3dEvents.onCollision,
		// Trigger on CollisionEnd
		View3dEvents.onCollisionEnd,
		// Trigger on Camera update
		View3dEvents.onCameraUpdated,
		// Trigger on Scene resized
		View3dEvents.onSceneResized,
	];

	// Set debug state.
	@property({ type: Boolean })
	public debug = false;

	// Use to scale canvas
	@property({ type: Boolean })
	public isMobile = false;

	/* eslint-disable @typescript-eslint/no-explicit-any */
	public emitEvent: any;
	public shadowRoot: any;
	/* eslint-enable @typescript-eslint/no-explicit-any */
	public getBoundingClientRect: () => DOMRect;

	protected scene: Scene | null = null;

	private isReady = false;
	private isDisconnected = false;
	private collisions = new Set<Report>();
	private pointerOver: string | Array<string>;
	private domRect: DOMRect;

	/**
	 * Renders the component.
	 */
	public render(): RenderResult {
		return html`
			<style>
				:host {
					display: flex;
					flex-direction: column;
					flex-grow: 1;
					flex-basis: auto;
					align-items: center;
					justify-content: center;
					overflow: hidden;
				}
				canvas {
					display: flex;
					flex-grow: 1;
					width: 100%;
					height: 100%;
					max-height: 100vw;
					outline: none;
					-webkit-tap-highlight-color: rgba(255, 255, 255, 0);
				}
			</style>
			<canvas></canvas>
		`;
	}

	/**
	 * Overwrites render.
	 */
	public update(): void {
		super.update();
		if (this.scene) {
			this.scene.debug = this.debug;
			return;
		}
		const canvas = this.shadowRoot.querySelector('canvas');
		this.scene = new Scene(canvas, this);
	}

	/**
	 * On scene ready delegate implementation.
	 */
	public onSceneReady(scene: Scene): void {
		if (this.isDisconnected) {
			/*
			 * This component disconnected before the scene initiated. Destroying scene and clearing up memory.
			 **/
			scene.destroy();
			return;
		}
		scene.resize(this.isMobile);

		scene.createGround();
		scene.startRenderLoop();
		this.isReady = true;
		scene.debug = this.debug;
		this.createCamera();
		this.dispatchEvent(
			new CustomEvent(View3dEvents.sceneReady, {
				bubbles: true,
			})
		);
	}

	/**
	 * On pointer over delegate implementation.
	 */
	public onPointerOver(nodeIds: string | Array<string>): void {
		if (this.pointerOver !== nodeIds) {
			if (nodeIds == TAG_GROUND) {
				this.dispatchEvent(
					new CustomEvent(View3dEvents.onPointerOverGround, {
						bubbles: true,
					})
				);
			} else {
				this.dispatchEvent(
					new CustomEvent(View3dEvents.onPointerOverNode, {
						bubbles: true,
						detail: { nodeIds },
					})
				);
			}
			this.pointerOver = nodeIds;
		}
	}

	/**
	 * On collision delegate implementation.
	 *
	 */
	public onCollision(collider: Report): void {
		this.emitCollisionEvent(collider);
	}

	/**
	 * On collision End delegate implementation.
	 *
	 */
	public onCollisionEnd(collider: Report): void {
		this.emitCollisionEndEvent(collider);
	}

	public onCameraUpdated(camera: Camera): void {
		this.dispatchEvent(
			new CustomEvent(View3dEvents.onCameraUpdated, {
				bubbles: true,
				detail: { camera },
			})
		);
	}

	/**
	 * On Scene resize delegate implementation.
	 *
	 */
	public onSceneResized(scene: Scene): void {
		this.dispatchEvent(
			new CustomEvent(View3dEvents.onSceneResized, {
				bubbles: true,
				detail: { scene },
			})
		);
	}

	/**
	 * Get if device uses iOS.
	 *
	 */
	public isIOS(): boolean {
		if (this.scene) {
			return this.scene.isIOS();
		}
		return false;
	}

	/**
	 * Preload models.
	 */
	public preload(urls: string[]): void {
		if (!this.isReady) {
			throw new Error(View3dErrors.sceneNotReady);
		}
		const promises = urls.map(async (url) => {
			// check if already preloaded
			if (this.isPreloaded(url)) {
				return null;
			}
			try {
				if (this.scene) {
					return await this.scene.preload(url);
				} else {
					return null;
				}
			} catch (error) {
				// preloading what we can, ignoring broken links
				return null;
			}
		});
		Promise.all(promises).then(() => {
			this.dispatchEvent(
				new CustomEvent(View3dEvents.modelsPreloaded, {
					bubbles: true,
					detail: { urls },
				})
			);
		});
	}

	/**
	 * Check if URL is preloaded.
	 */
	public isPreloaded(url: string): boolean {
		if (this.scene) {
			return this.scene.isPreloaded(url);
		}
		throw 'View3d: isPreloaded(): scene does not exist';
	}

	/**
	 * Preload assets package.
	 */
	public preloadPackage(assetsPackage: AssetsPackage): void {
		if (this.scene) {
			this.scene.preloadPackage(assetsPackage).then((assembiles) => {
				const urls = assembiles.map((a) => a.name);
				this.dispatchEvent(
					new CustomEvent(View3dEvents.modelsPreloaded, {
						bubbles: true,
						detail: { urls },
					})
				);
			});
		}
	}

	/**
	 * Create Object on scene.
	 */
	public createObject(
		id: string,
		pos: TransformPosition,
		url?: string,
		interactor?: BoxDimensions | string,
		collider?: BoxDimensions
	): Node {
		if (!this.isReady) {
			// eslint-disable-next-line no-console
			throw console.error(View3dErrors.sceneNotReady);
		}

		if (this.scene) {
			const node = this.scene.createNode(id, pos);
			if (url && this.scene.isPreloaded(url)) {
				const meshRenderer = node.addComponent(MeshRenderer, 'MeshRenderer');
				meshRenderer.state.mesh = url;
			}
			if (interactor) {
				node.addComponent(Interactor, 'Interactor').state.mesh = interactor;
			}
			if (collider) {
				node.addComponent(Collider, 'Collider').state.mesh = collider;
			}
			return node;
		}

		throw 'View3d: createObject(): scene does not exist.';
	}

	public createLight(direction = new TransformVector(0, 1, 0), intensity = 1.0): void {
		if (this.scene) {
			this.scene.createLight(direction, intensity);
		}
	}

	public createEnvironment(url: string, intensity = 1.0): void {
		if (this.scene) {
			this.scene.createEnvironment(url, intensity);
		}
	}

	/**
	 * Get Object on scene.
	 */
	public getObject(id: string): Node | false {
		if (this.scene) {
			return this.scene.sceneNodes.get(id) || false;
		}
		throw 'View3d: getObject(): scene does not exist';
	}

	/**
	 * Get Object on scene.
	 */
	public destroyObjectById(id: string): boolean {
		if (this.scene) {
			const node = this.scene.sceneNodes.get(id);
			if (node) {
				this.scene.destroyNode(id);
				return true;
			} else {
				return false;
			}
		}
		throw 'View3d: destroyObjectById(): scene does not exist';
	}

	public destroyObject(object: Node): boolean {
		if (this.scene) {
			if (object) {
				this.scene.destroyNode(object.id);
				return true;
			} else {
				return false;
			}
		}
		throw 'View3d: destroyObject(): scene does not exist';
	}

	/**
	 * Set position of object on scene.
	 */
	public setObjectPosition(id: string, pos: TransformPosition): void {
		if (this.scene) {
			const node = this.scene.sceneNodes.get(id);
			if (node) {
				node.setAbsolutePosition(pos);
			}
		}
		throw 'View3d: setObjectPosition(): scene does not exist';
	}

	/**
	 * Set position of ground on scene.
	 */
	public setGroundPosition(pos: TransformPosition): void {
		const ground = this.scene ? this.scene.basicElements.get(TAG_GROUND) : null;
		if (ground) {
			ground.worldPosition = pos;
		}
	}

	/**
	 * Creates new Camera on scene.
	 */
	public createCamera(id = 'Camera'): Camera {
		if (this.scene) {
			return this.scene.createCamera(id);
		}
		throw 'View3d: createCamera(): scene does not exist';
	}

	/**
	 * Get Camera on scene.
	 */
	public getCamera(id = 'Camera'): Camera {
		if (this.scene) {
			return this.scene.cameras.get(id) as Camera;
		}
		throw 'View3d: getCamera(): scene does not exist';
	}

	public enableCameraControl(): void {
		if (this.scene) {
			this.scene.controlCamera = true;
		}
	}

	public disableCameraControl(): void {
		if (this.scene) {
			this.scene.controlCamera = false;
		}
	}

	public showDebug(visible: boolean, elementId: string): void {
		if (this.scene) {
			this.scene.showDebug(visible, elementId);
		}
		throw 'View3d: showDebug(): scene does not exist';
	}

	/**
	 * Get Pointer Position.
	 */
	public getPointerHit(x: number, y: number, target?: string): PointerHit | false {
		if (this.scene) {
			if (this.domRect.y) {
				y -= this.domRect.y;
			}
			if (this.domRect.x) {
				x -= this.domRect.x;
			}
			if (target) {
				return this.scene.getPointerHit(x, y, target);
			} else {
				return this.scene.getPointerHit(x, y);
			}
		}
		throw 'View3d: getPointerHit(): scene does not exist';
	}

	/**
	 * Triggered when the element is connected.
	 */
	protected _connected = (): void => {
		this.domRect = this.getBoundingClientRect();
	};

	/**
	 * Triggered when the component is removed from DOM.
	 */
	protected _disconnected = (): void => {
		this.isDisconnected = true;

		if (this.scene) {
			this.scene.stopRenderLoop();
			this.scene.destroy();
		}
	};

	/**
	 * Emit collision events.
	 */
	private emitCollisionEvent(collider: Report): void {
		if (!this.collisions.has(collider)) {
			this.dispatchEvent(
				new CustomEvent(View3dEvents.onCollision, {
					bubbles: true,
					detail: { collider },
				})
			);
			this.collisions.add(collider);
		}
	}

	/**
	 * Emit collision events.
	 */
	private emitCollisionEndEvent(collider: Report): void {
		if (this.collisions.has(collider)) {
			this.dispatchEvent(
				new CustomEvent(View3dEvents.onCollisionEnd, {
					bubbles: true,
					detail: { collider },
				})
			);
			this.collisions.delete(collider);
		}
	}

	/**
	 * Resize.
	 */
	public resize: () => void = () => {
		if (this.scene) {
			this.scene.resize(this.isMobile);
			this.update();
			this.domRect = this.getBoundingClientRect();
		}
	};
}

/* Ignore the error on the next line, does not affect the application */
customElements.define('kompis-view-3d', View3d);
